// components/FirmwareUpload.tsx
import React, { useEffect, useRef, useState } from 'react';
import FirmwareFlasher from './FirmwareFlasher';
import './FirmwareUpload.css';

declare global {
  interface Navigator {
    serial?: any;
  }
}

interface BinFile {
  name: string;
  download_url: string;
  size: number;
}

const GITHUB_API = 'https://api.github.com/repos/kdi6033/download/contents/';
const RAW_BASE = 'https://raw.githubusercontent.com/kdi6033/download/main/';

// 모델 사진: download 저장소의 images/모델이름.png (펌웨어 이름에서 .ino.merged.bin 을 뗀 이름)
// 사진이 없으면 모델 이름만 보여 준다.
const MODEL_DESC: Record<string, string> = {
  'i2r-02': 'IoT PLC (4ch)',
  'i2r-02-hmi': 'IoT PLC (4ch) HMI',
  'i2r-03': 'IoT PLC (4ch 센서)',
};
const modelName = (f: BinFile) => f.name.replace('.ino.merged.bin', '');

const FirmwareUpload: React.FC = () => {
  const [binFiles, setBinFiles]     = useState<BinFile[]>([]);
  const [filesError, setFilesError] = useState('');
  const [pickedModel, setPickedModel] = useState<string | null>(null); // 사진을 눌러 고른 모델
  const [pickCount, setPickCount]   = useState(0);                      // 사진을 누른 횟수 (누를 때마다 포트 선택 창을 다시 띄운다)
  const supported = typeof navigator !== 'undefined' && !!navigator.serial;  // Web Serial 지원 여부
  const flasherRef = useRef<HTMLDivElement>(null);                      // 설치 창 (사진을 누르면 화면에 보이게 스크롤)

  useEffect(() => {
    fetch(GITHUB_API)
      .then(r => { if (!r.ok) throw new Error(`GitHub API ${r.status}`); return r.json(); })
      .then((data: any[]) => {
        const bins = data.filter(f => f.name.endsWith('.bin'));
        setBinFiles(bins);
        if (!bins.length) setFilesError('저장소에 .bin 파일이 없습니다.');
      })
      .catch(() => setFilesError('GitHub 파일 목록을 가져올 수 없습니다.'));
  }, []);

  // 사진을 누르면 아래에 열리는 설치 창이 화면 안에 보이게 한다 (모델이 많아 카드가 길 때)
  useEffect(() => {
    if (pickedModel) setTimeout(() => flasherRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  }, [pickedModel, pickCount]);

  // 사진을 누르면 그 모델의 설치 창을 열고, 바로 COM 포트 선택 창을 띄운다
  const pickModel = (name: string) => {
    setPickedModel(name);
    setPickCount((c) => c + 1);
  };

  return (
    <div className="fw-wrap">
      <h2 className="fw-title">
        펌웨어 다운로드
        <a
          href="https://youtu.be/UWvJT5ORa_A"
          target="_blank"
          rel="noopener noreferrer"
          className="fw-youtube-link"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" style={{flexShrink: 0}}>
            <path fill="#ff0000" d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8z"/>
            <path fill="#fff" d="M9.7 15.5l6.3-3.5-6.3-3.5v7z"/>
          </svg>
          펌웨어 다운로드 방법
        </a>
      </h2>
      <p className="fw-info">
        크롬(Chrome) 또는 엣지(Edge) 브라우저에서만 동작합니다.<br />
        ESP32 보드를 USB로 연결한 후 포트를 선택하고 펌웨어를 선택하세요.
      </p>

      {/* 모델 선택: 사진을 누르면 아래에 설치 창이 열린다 */}
      <div className="fw-model-ref">
        <p className="fw-model-ref-title">그림을 보고 모델을 눌러 주세요</p>
        {filesError ? (
          <p className="fw-warn">{filesError}</p>
        ) : binFiles.length === 0 ? (
          <p className="fw-loading">모델 목록 불러오는 중...</p>
        ) : (
          <div className="fw-model-grid">
            {binFiles.filter(f => f.name.endsWith('.merged.bin')).map(f => {
              const name = modelName(f);
              const picked = pickedModel === name;
              return (
                <div
                  key={f.name}
                  className="fw-model-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => pickModel(name)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickModel(name); }}
                  style={{ cursor: 'pointer', outline: picked ? '3px solid #2563eb' : undefined }}
                >
                  <img
                    src={`${RAW_BASE}images/${name}.png`}
                    alt={name}
                    className="fw-model-img"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                  />
                  <div className="fw-model-name">{name}</div>
                  <div className="fw-model-desc">{MODEL_DESC[name] || ''}</div>
                </div>
              );
            })}
          </div>
        )}
        {pickedModel && (
          <div ref={flasherRef} style={{ marginTop: '16px' }}>
            <FirmwareFlasher key={`${pickedModel}-${pickCount}`} autoStart title={pickedModel} fileUrls={[`${RAW_BASE}${pickedModel}.ino.merged.bin`]} />
          </div>
        )}
        <p className="fw-model-more">
          <a href="https://github.com/kdi6033/i2r/blob/main/README.md#i2r-iot-plc--sensor"
             target="_blank" rel="noreferrer" className="fw-model-more-link">
            🔍 기타 기기 모델 보기 →
          </a>
        </p>
      </div>

      {!supported && (
        <p className="fw-warn">
          이 브라우저는 Web Serial API를 지원하지 않습니다. Chrome 또는 Edge를 사용하세요.
        </p>
      )}
    </div>
  );
};

export default FirmwareUpload;
